import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_8c664764 from 'nuxt_plugin_sentryserver_8c664764' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_54136ac6 from 'nuxt_plugin_sentryclient_54136ac6' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_saveInitialData_54e5ae77 from 'nuxt_plugin_saveInitialData_54e5ae77' // Source: ./data-fetcher/boot/saveInitialData.ts (mode: 'all')
import nuxt_plugin_initSettings_6e0dc462 from 'nuxt_plugin_initSettings_6e0dc462' // Source: ./initChannel/boot/initSettings.ts (mode: 'all')
import nuxt_plugin_publicPathclient_92fb654c from 'nuxt_plugin_publicPathclient_92fb654c' // Source: ./initChannel/boot/publicPath.client.ts (mode: 'client')
import nuxt_plugin_initChannel_4424fbe4 from 'nuxt_plugin_initChannel_4424fbe4' // Source: ./initChannel/plugins/initChannel.ts (mode: 'all')
import nuxt_plugin_fetchPageData_25b69595 from 'nuxt_plugin_fetchPageData_25b69595' // Source: ./data-fetcher/boot/fetchPageData.ts (mode: 'all')
import nuxt_plugin_nuxtuseragent_14018cd3 from 'nuxt_plugin_nuxtuseragent_14018cd3' // Source: ./nuxt-user-agent.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_1a5c9232 from 'nuxt_plugin_cookieuniversalnuxt_1a5c9232' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_router_46d81665 from 'nuxt_plugin_router_46d81665' // Source: ./router.js (mode: 'all')
import nuxt_plugin_eventHandler_4d0614de from 'nuxt_plugin_eventHandler_4d0614de' // Source: ../src/plugins/eventHandler (mode: 'all')
import nuxt_plugin_instanceConnector_06dadbff from 'nuxt_plugin_instanceConnector_06dadbff' // Source: ../src/plugins/instance-connector/instanceConnector (mode: 'all')
import nuxt_plugin_i18n_259a055f from 'nuxt_plugin_i18n_259a055f' // Source: ../src/plugins/i18n (mode: 'all')
import nuxt_plugin_portalCore_3348ecac from 'nuxt_plugin_portalCore_3348ecac' // Source: ../src/plugins/portalCore (mode: 'all')
import nuxt_plugin_paywall_8bc59128 from 'nuxt_plugin_paywall_8bc59128' // Source: ../src/modules/paywall/plugins/paywall (mode: 'all')
import nuxt_plugin_customer_4041fc04 from 'nuxt_plugin_customer_4041fc04' // Source: ../src/modules/customer/plugins/customer (mode: 'client')
import nuxt_plugin_globalComponentsImporter_63cb3a0c from 'nuxt_plugin_globalComponentsImporter_63cb3a0c' // Source: ../src/plugins/globalComponentsImporter (mode: 'all')
import nuxt_plugin_filters_52493d16 from 'nuxt_plugin_filters_52493d16' // Source: ../src/common/filters/filters (mode: 'all')
import nuxt_plugin_jsonld_2c9e1f61 from 'nuxt_plugin_jsonld_2c9e1f61' // Source: ../src/plugins/jsonld (mode: 'all')
import nuxt_plugin_portalAds_26682648 from 'nuxt_plugin_portalAds_26682648' // Source: ../src/modules/ads/plugins/portalAds (mode: 'client')
import nuxt_plugin_analyticsTrackers_ecea9622 from 'nuxt_plugin_analyticsTrackers_ecea9622' // Source: ../src/modules/analytics/plugins/analyticsTrackers (mode: 'client')
import nuxt_plugin_externalizedPackages_76a2af29 from 'nuxt_plugin_externalizedPackages_76a2af29' // Source: ../src/plugins/externalizedPackages (mode: 'client')
import nuxt_plugin_oneSignal_58c50be6 from 'nuxt_plugin_oneSignal_58c50be6' // Source: ../src/plugins/oneSignal (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"portal-root","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"},{"hid":"description","name":"description","content":"Frontend for digital newspapers and magazines powered by Vue.js"}],"link":[{"rel":"stylesheet","hid":"inter_font","href":"https:\u002F\u002Fg.delfi.ee\u002Fportalcore\u002Ffonts\u002Finter\u002Finter.css"}],"script":[{"src":"https:\u002F\u002Fg.delfi.lt\u002Fscms\u002F?g=j"},{"src":"https:\u002F\u002Flt-production-login-api-client.s3-website.teliahybridcloud.com\u002Flt.js"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_8c664764 === 'function') {
    await nuxt_plugin_sentryserver_8c664764(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_54136ac6 === 'function') {
    await nuxt_plugin_sentryclient_54136ac6(app.context, inject)
  }

  if (typeof nuxt_plugin_saveInitialData_54e5ae77 === 'function') {
    await nuxt_plugin_saveInitialData_54e5ae77(app.context, inject)
  }

  if (typeof nuxt_plugin_initSettings_6e0dc462 === 'function') {
    await nuxt_plugin_initSettings_6e0dc462(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_publicPathclient_92fb654c === 'function') {
    await nuxt_plugin_publicPathclient_92fb654c(app.context, inject)
  }

  if (typeof nuxt_plugin_initChannel_4424fbe4 === 'function') {
    await nuxt_plugin_initChannel_4424fbe4(app.context, inject)
  }

  if (typeof nuxt_plugin_fetchPageData_25b69595 === 'function') {
    await nuxt_plugin_fetchPageData_25b69595(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtuseragent_14018cd3 === 'function') {
    await nuxt_plugin_nuxtuseragent_14018cd3(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_1a5c9232 === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_1a5c9232(app.context, inject)
  }

  if (typeof nuxt_plugin_router_46d81665 === 'function') {
    await nuxt_plugin_router_46d81665(app.context, inject)
  }

  if (typeof nuxt_plugin_eventHandler_4d0614de === 'function') {
    await nuxt_plugin_eventHandler_4d0614de(app.context, inject)
  }

  if (typeof nuxt_plugin_instanceConnector_06dadbff === 'function') {
    await nuxt_plugin_instanceConnector_06dadbff(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_259a055f === 'function') {
    await nuxt_plugin_i18n_259a055f(app.context, inject)
  }

  if (typeof nuxt_plugin_portalCore_3348ecac === 'function') {
    await nuxt_plugin_portalCore_3348ecac(app.context, inject)
  }

  if (typeof nuxt_plugin_paywall_8bc59128 === 'function') {
    await nuxt_plugin_paywall_8bc59128(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_customer_4041fc04 === 'function') {
    await nuxt_plugin_customer_4041fc04(app.context, inject)
  }

  if (typeof nuxt_plugin_globalComponentsImporter_63cb3a0c === 'function') {
    await nuxt_plugin_globalComponentsImporter_63cb3a0c(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_52493d16 === 'function') {
    await nuxt_plugin_filters_52493d16(app.context, inject)
  }

  if (typeof nuxt_plugin_jsonld_2c9e1f61 === 'function') {
    await nuxt_plugin_jsonld_2c9e1f61(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_portalAds_26682648 === 'function') {
    await nuxt_plugin_portalAds_26682648(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_analyticsTrackers_ecea9622 === 'function') {
    await nuxt_plugin_analyticsTrackers_ecea9622(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_externalizedPackages_76a2af29 === 'function') {
    await nuxt_plugin_externalizedPackages_76a2af29(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_oneSignal_58c50be6 === 'function') {
    await nuxt_plugin_oneSignal_58c50be6(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
